/**
 * Garde de route côté client.
 *
 * Elle évite d'afficher un écran vide à un visiteur, et **rien d'autre** : la
 * protection réelle est côté serveur (docs/07 §8). Un utilisateur curieux qui
 * force la route ne verra que des appels d'API refusés.
 */
import { Navigate, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useRef, type ReactNode } from "react";

import { useSessionStore } from "@/shared/auth/session";
import { Skeleton } from "@/shared/ui/Skeleton";

interface RequireAuthProps {
  children: ReactNode;
}

export function RequireAuth({ children }: RequireAuthProps) {
  const { t } = useTranslation("auth");
  const status = useSessionStore((state) => state.status);
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  // Figée au montage : une fois la redirection engagée, `pathname` vaut déjà
  // `/login`, et l'on renverrait l'utilisateur vers la page de connexion après
  // s'y être connecté.
  const intended = useRef(pathname);

  // Tant que la restauration silencieuse est en vol, on n'affiche NI la page NI
  // la redirection : rediriger ici renverrait vers `/login` toute personne qui
  // recharge une page protégée alors qu'elle est parfaitement authentifiée.
  if (status === "unknown") {
    return (
      <div className="space-y-3" aria-busy="true">
        <Skeleton className="h-6 w-1/3" label={t("session.restoring")} />
        <Skeleton className="h-4 w-2/3" label={t("session.restoring")} />
      </div>
    );
  }

  if (status === "anonymous") {
    // `redirect` est conservé pour ramener l'utilisateur là où il allait après
    // connexion — sinon il atterrit sur l'accueil et doit renaviguer.
    return <Navigate to="/login" search={{ redirect: intended.current }} replace />;
  }

  return <>{children}</>;
}
